import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  HiDownload,
  HiArrowLeft,
  HiSearch
} from 'react-icons/hi';
import Select from './Select';
import Notification from './Notification';
import ShippingLabelParcel from './ShippingLabelParcel';
import apiService from '../services/api';
import { normalizeApiData } from '../utils/apiHelpers';
import { exportLabelPaperToPng } from '../utils/labelExport';
import { useNotification } from '../hooks/useNotification';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
import './Labels.css';

const Labels = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { notifications, removeNotification, showWarning, showSuccess } = useNotification();
  const [boxes, setBoxes] = useState([]);
  const [selectedBoxId, setSelectedBoxId] = useState('');
  const [box, setBox] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingLabel, setDownloadingLabel] = useState(false);
  const labelPaperRef = useRef(null);

  useEffect(() => {
    loadBoxes();
  }, []);

  useRealtimeRefresh(['packages', 'invoices'], () => loadBoxes());

  /* Permite CSS de impressão exclusivo 10×15 cm sem afetar outras telas */
  useEffect(() => {
    document.documentElement.classList.add('labels-print-page');
    document.body.classList.add('labels-print-page');
    return () => {
      document.documentElement.classList.remove('labels-print-page');
      document.body.classList.remove('labels-print-page');
    };
  }, []);

  // Ao carregar as caixas, preencher o select com o box da URL (ex: /labels?box=10) e já gerar a etiqueta
  useEffect(() => {
    const boxIdFromUrl = searchParams.get('box');
    // Não depender da lista local (que pode estar paginada). Se veio na URL, assumir como seleção.
    if (boxIdFromUrl && !selectedBoxId) {
      setSelectedBoxId(String(boxIdFromUrl));
    }
  }, [boxes, searchParams, selectedBoxId]);

  const loadBoxes = async () => {
    try {
      const data = await apiService.getBoxes();
      const boxesList = normalizeApiData(data);
      // Mapear os campos da API para o formato esperado pelo componente
      const boxesWithDefaults = boxesList.map(box => ({
        ...box,
        // Mapear codigo_pacote para codigo
        codigo: box.codigo_pacote || box.codigo || '',
        // Mapear cliente.nome para cliente_nome
        cliente_nome: box.cliente?.nome || box.cliente_nome || '',
        // Mapear cliente_id
        cliente_id: box.cliente_id || box.cliente?.id || '',
        // Mapear dimensões separadas para objeto dimensoes
        dimensoes: {
          comprimento: box.comprimento || box.dimensoes?.comprimento || '',
          largura: box.largura || box.dimensoes?.largura || '',
          altura: box.altura || box.dimensoes?.altura || ''
        },
        // Mapear peso_bruto para peso
        peso: box.peso_bruto || box.peso || '',
        // Mapear valor_declarado para valor_remessa
        valor_remessa: box.valor_declarado || box.valor_remessa || '',
        // Preservar status da API
        status: box.status || 'pedido_criado'
      }));
      setBoxes(boxesWithDefaults);
      setLoading(false);
    } catch (error) {
      setBoxes([]);
      setLoading(false);
    }
  };

  const loadBox = useCallback(async () => {
    if (!selectedBoxId) {
      setBox(null);
      setInvoice(null);
      return;
    }

    const maybeId = Number(selectedBoxId);
    const hasNumericId = Number.isFinite(maybeId) && maybeId > 0;
    const errorDetails = (err) => {
      if (!err) return '';
      const msg = err?.message ? String(err.message) : '';
      const status = err?.status ?? err?.response?.status;
      const statusText = status ? ` (HTTP ${status})` : '';
      if (!msg) return statusText;
      return `${statusText}${statusText && msg ? ' — ' : ''}${msg}`;
    };

    const parseLabelResponse = (labelData) => {
      // Backends diferentes podem retornar:
      // - { box: {...}, invoice: {...} }
      // - { data: { box: {...}, invoice: {...} } }
      // - { ...boxFields, invoice: {...} } (box "na raiz")
      // - { data: {...boxFields} }
      const root = labelData?.data && typeof labelData.data === 'object' ? labelData.data : labelData;
      const boxCandidate =
        root?.box && typeof root.box === 'object'
          ? root.box
          : root && typeof root === 'object' && ('id' in root || 'codigo' in root || 'codigo_pacote' in root)
            ? root
            : null;
      const invoiceCandidate =
        root?.invoice && typeof root.invoice === 'object'
          ? root.invoice
          : null;
      return { box: boxCandidate, invoice: invoiceCandidate };
    };

    const hasText = (v) => v != null && String(v).trim() !== '';

    const hasOrigemDestino = (b, inv) => {
      return (
        hasText(b?.origem) ||
        hasText(b?.destino) ||
        hasText(b?.origin) ||
        hasText(b?.destination) ||
        hasText(b?.tarifa?.origem) ||
        hasText(b?.tarifa?.destino) ||
        hasText(inv?.origem) ||
        hasText(inv?.destino) ||
        hasText(inv?.tarifa?.origem) ||
        hasText(inv?.tarifa?.destino)
      );
    };

    const mergeRouteFields = (target, source) => {
      if (!target || !source) return target || source || null;
      const next = { ...target };
      if (!hasText(next.origem) && hasText(source.origem)) next.origem = source.origem;
      if (!hasText(next.destino) && hasText(source.destino)) next.destino = source.destino;
      if (!hasText(next.origin) && hasText(source.origin)) next.origin = source.origin;
      if (!hasText(next.destination) && hasText(source.destination)) next.destination = source.destination;
      if ((!next.tarifa || typeof next.tarifa !== 'object') && source.tarifa && typeof source.tarifa === 'object') {
        next.tarifa = source.tarifa;
      } else if (next.tarifa && source.tarifa) {
        next.tarifa = {
          ...source.tarifa,
          ...next.tarifa,
          origem: hasText(next.tarifa.origem) ? next.tarifa.origem : source.tarifa.origem,
          destino: hasText(next.tarifa.destino) ? next.tarifa.destino : source.tarifa.destino,
        };
      }
      if (!next.id && source.id) next.id = source.id;
      if (!hasText(next.codigo_pacote) && hasText(source.codigo_pacote)) {
        next.codigo_pacote = source.codigo_pacote;
      }
      return next;
    };

    const enrichWithPackageTarifa = async (boxObj) => {
      if (!boxObj?.id) return boxObj;
      try {
        const pkg = await apiService.getPackage(Number(boxObj.id));
        const root = pkg?.data && typeof pkg.data === 'object' ? pkg.data : pkg;
        let next = mergeRouteFields(boxObj, root);
        const tarifa = root?.tarifa && typeof root.tarifa === 'object' ? root.tarifa : null;
        if (tarifa) {
          next = mergeRouteFields(next, { tarifa, origem: tarifa.origem, destino: tarifa.destino });
        }
        // Fallback: pacote sem origem/destino preenchidos → usar tarifa
        if (next?.tarifa) {
          if (!hasText(next.origem) && hasText(next.tarifa.origem)) next.origem = next.tarifa.origem;
          if (!hasText(next.destino) && hasText(next.tarifa.destino)) next.destino = next.tarifa.destino;
        }
        return next;
      } catch {
        return boxObj;
      }
    };

    try {
      // Preferir buscar diretamente pela API quando a seleção parece ser um id numérico.
      // Isso evita depender de uma lista paginada (problema ao vir da 2ª+ página).
      if (hasNumericId) {
        try {
          const labelData = await apiService.getLabel(maybeId);
          const parsed = parseLabelResponse(labelData);
          const listBox = Array.isArray(boxes)
            ? boxes.find((b) => Number(b.id) === maybeId)
            : null;
          let enrichedBox = parsed.box
            ? await enrichWithPackageTarifa(mergeRouteFields(parsed.box, listBox))
            : listBox || null;
          const invoiceData = parsed.invoice;
          if (enrichedBox && invoiceData) {
            enrichedBox = mergeRouteFields(enrichedBox, {
              origem: invoiceData.origem,
              destino: invoiceData.destino,
            });
          }
          if (enrichedBox && !hasOrigemDestino(enrichedBox, invoiceData)) {
            enrichedBox = await enrichWithPackageTarifa(enrichedBox);
          }
          setBox(enrichedBox);
          setInvoice(invoiceData);
          return;
        } catch (labelError) {
          showWarning(`Não foi possível carregar os dados completos da etiqueta.${errorDetails(labelError) ? ` Detalhes: ${errorDetails(labelError)}` : ''} Exibindo informações básicas.`);
          // Se falhar (404/500/etc), continuar para fallback local abaixo.
        }
      }

      // Fallback: tentar localizar na lista carregada (por id ou código) e, se existir id, buscar etiqueta por ele.
      const foundBox = Array.isArray(boxes)
        ? boxes.find(b => b.id === maybeId || b.codigo === selectedBoxId || b.codigo_pacote === selectedBoxId)
        : null;

      if (foundBox && foundBox.id) {
        try {
          const labelData = await apiService.getLabel(foundBox.id);
          const parsed = parseLabelResponse(labelData);
          const baseBox = mergeRouteFields(parsed.box || foundBox, foundBox);
          let enrichedBox = await enrichWithPackageTarifa(baseBox);
          const invoiceData = parsed.invoice || null;
          if (enrichedBox && invoiceData) {
            enrichedBox = mergeRouteFields(enrichedBox, {
              origem: invoiceData.origem,
              destino: invoiceData.destino,
            });
          }
          setBox(enrichedBox);
          setInvoice(invoiceData);
          return;
        } catch (labelError) {
          const isServerError = labelError.status === 500 ||
                               labelError.isServerError ||
                               (labelError.response && labelError.response.status === 500) ||
                               (labelError.message && labelError.message.includes('Server Error'));

          if (isServerError) {
            setBox(foundBox);
            setInvoice(null);
            showWarning(`Não foi possível carregar os dados completos da etiqueta.${errorDetails(labelError) ? ` Detalhes: ${errorDetails(labelError)}` : ''} Exibindo informações básicas.`);
            return;
          }

          setBox(foundBox);
          setInvoice(null);
          if (labelError.status === 404) {
            showWarning(`Etiqueta não encontrada no servidor.${errorDetails(labelError) ? ` Detalhes: ${errorDetails(labelError)}` : ''} Exibindo informações básicas.`);
          } else {
            showWarning(`Não foi possível carregar os dados completos da etiqueta.${errorDetails(labelError) ? ` Detalhes: ${errorDetails(labelError)}` : ''} Exibindo informações básicas.`);
          }
          return;
        }
      }

      setBox(null);
      setInvoice(null);
    } catch (error) {
      showWarning('Erro ao carregar informações da etiqueta. Por favor, tente novamente.');
      setBox(null);
      setInvoice(null);
    }
  }, [boxes, selectedBoxId, showWarning]);

  useEffect(() => {
    if (selectedBoxId) {
      loadBox();
    }
  }, [selectedBoxId, loadBox]);

  const codigoEtiqueta = useMemo(() => {
    if (!box) return '';
    return (
      box.codigo_pacote ||
      box.codigo ||
      box.codigo_rastreio ||
      ''
    ).toString();
  }, [box]);

  const handleDownloadLabelImage = async () => {
    const node = labelPaperRef.current;
    if (!node) {
      showWarning('Etiqueta não encontrada para exportar.');
      return;
    }
    try {
      setDownloadingLabel(true);
      const name = (codigoEtiqueta || box?.codigo || 'etiqueta').toString();
      await exportLabelPaperToPng(node, name);
      showSuccess('Imagem baixada. Dimensões 10×15 cm ao imprimir em escala 100%.');
    } catch (err) {
      showWarning(err?.message || 'Não foi possível gerar a imagem da etiqueta.');
    } finally {
      setDownloadingLabel(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem', padding: '2rem' }}>
          <div className="loading-spinner-large"></div>
          <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>Carregando etiquetas...</p>
        </div>
      </div>
    );
  }

  const handleBoxChange = (e) => {
    const value = e.target.value;
    setSelectedBoxId(value);
    if (value) {
      navigate(`/labels?box=${value}`, { replace: true });
    } else {
      navigate('/labels', { replace: true });
    }
  };

  return (
    <div className="page-container">
      <div className="breadcrumb">
        <span>Painel</span>
        <span>Caixas / Malas</span>
        <span>Etiqueta</span>
      </div>

      <div className="page-header">
        <div>
          <h1>Etiqueta de Envio</h1>
          <p>Selecione uma caixa/mala para gerar a etiqueta</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={() => navigate('/boxes')}>
            <HiArrowLeft size={18} />
            Voltar
          </button>
        </div>
      </div>

      <div className="label-selector-section">
        <div className="form-group">
          <label>
            <HiSearch size={18} />
            Selecionar Caixa/Mala
          </label>
          <Select
            value={selectedBoxId}
            onChange={handleBoxChange}
            placeholder="Selecione uma caixa/mala"
            options={(() => {
              const base = Array.isArray(boxes)
                ? boxes.map(b => ({
                    value: String(b.id),
                    label: `${b.codigo} - ${b.cliente_nome} (${b.tipo === 'caixa' ? 'Caixa' : 'Mala'})`
                  }))
                : [];
              if (!selectedBoxId) return base;
              const exists = base.some(o => String(o.value) === String(selectedBoxId));
              if (exists) return base;
              return [
                { value: String(selectedBoxId), label: `#${selectedBoxId} (carregado pela URL)` },
                ...base
              ];
            })()}
          />
        </div>
      </div>

      {!box && selectedBoxId && (
        <div className="empty-state">
          <p>Caixa/Mala não encontrada</p>
        </div>
      )}

      {!selectedBoxId && (
        <div className="empty-state">
          <p>Selecione uma caixa/mala para visualizar a etiqueta</p>
        </div>
      )}

      {box && (
        <>
          <div className="page-header">
            <div>
              <h2>Etiqueta - {codigoEtiqueta || box.codigo}</h2>
              <p>Origem e destino da remessa</p>
            </div>
            <div className="header-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleDownloadLabelImage}
                disabled={downloadingLabel}
              >
                <HiDownload size={18} />
                {downloadingLabel ? 'Gerando…' : 'Baixar'}
              </button>
            </div>
          </div>

      <div className="label-container">
        <ShippingLabelParcel
          ref={labelPaperRef}
          box={box}
          invoice={invoice}
          exportAreaId="label-export-area"
        />
      </div>
        </>
      )}

      <div className="notifications-container">
        {notifications.map(notification => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default Labels;
